import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Briefcase, FileSignature, MessageSquare, Plus, Search, User, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

import { useSession } from './useSession';
import { useTeamMember } from './useTeamMember';
import {
  CLIENT_TABS,
  FOOTER_NAV,
  HIDDEN_NAV,
  PRIMARY_NAV,
} from '../components/Sidebar/navItems';
import { projectStatusLabel } from '../modules/delivery/projects/projects.schema';
import { supabase } from '../lib/supabase';
import { cn } from '../lib/cn';
import styles from './CommandPalette.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

type ResultKind = 'action' | 'page' | 'lead' | 'customer' | 'project' | 'quote' | 'conversation';

interface Result {
  kind: ResultKind;
  id: string;
  title: string;
  subtitle: string;
  to: string;
}

const KIND_LABELS: Record<ResultKind, string> = {
  action: 'פעולות',
  page: 'דפים',
  lead: 'לידים',
  customer: 'לקוחות',
  project: 'פרויקטים',
  quote: 'הצעות מחיר',
  conversation: 'שיחות בוט',
};

const KIND_ICONS: Record<ResultKind, LucideIcon> = {
  action: Plus,
  page: ArrowLeft,
  lead: User,
  customer: Users,
  project: Briefcase,
  quote: FileSignature,
  conversation: MessageSquare,
};

const KIND_ORDER: ResultKind[] = ['action', 'page', 'lead', 'customer', 'project', 'quote', 'conversation'];

/** Create actions land on the owning page with `?new=1`, which opens its drawer. */
const ACTIONS: Result[] = [
  { kind: 'action', id: 'new-customer', title: 'צור לקוח', subtitle: 'ניהול לקוח', to: '/customers?new=1' },
  { kind: 'action', id: 'new-project', title: 'צור פרויקט', subtitle: 'ניהול לקוח', to: '/projects?new=1' },
  { kind: 'action', id: 'new-task', title: 'צור משימה', subtitle: 'ניהול לקוח', to: '/tasks?new=1' },
  { kind: 'action', id: 'new-quote', title: 'צור הצעת מחיר', subtitle: 'ניהול לקוח', to: '/quotes?new=1' },
];

const PAGES: Result[] = [
  ...PRIMARY_NAV.filter((i) => i.id !== 'client-management').map((i) => ({ id: i.id, title: i.label, path: i.path, sub: i.wip === true ? 'בבנייה' : '' })),
  ...CLIENT_TABS.map((t) => ({ id: `client-${t.path}`, title: t.label, path: t.path, sub: 'ניהול לקוח' })),
  ...HIDDEN_NAV.map((i) => ({ id: i.id, title: i.label, path: i.path, sub: 'בבנייה' })),
  ...FOOTER_NAV.map((i) => ({ id: i.id, title: i.label, path: i.path, sub: '' })),
].map((p) => ({ kind: 'page' as const, id: p.id, title: p.title, subtitle: p.sub, to: p.path }));

function matchStatic(term: string): Result[] {
  const q = term.trim().toLowerCase();
  // Empty query: the create actions, so Ctrl+K is useful before typing.
  if (!q) return ACTIONS;
  return [...ACTIONS, ...PAGES].filter(
    (r) => r.title.toLowerCase().includes(q) || r.subtitle.toLowerCase().includes(q),
  );
}

function sanitize(term: string): string {
  return term.replace(/[%(),]/g, '').trim();
}

async function fetchResults(orgId: string, term: string): Promise<Result[]> {
  const q = sanitize(term);
  if (q.length < 2) return [];
  const like = `%${q}%`;

  const [leadsRes, customersRes, projectsRes, quotesRes, convsRes] = await Promise.all([
    supabase
      .from('leads')
      .select('id, full_name, company_name, phone, email, status')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .or(`full_name.ilike.${like},company_name.ilike.${like},email.ilike.${like},phone.ilike.${like}`)
      .limit(6),
    supabase
      .from('companies')
      .select('id, name, industry')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .ilike('name', like)
      .limit(6),
    supabase
      .from('projects')
      .select('id, name, status')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .ilike('name', like)
      .limit(6),
    supabase
      .from('quotes')
      .select('id, quote_number, status, value_amount')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .ilike('quote_number', like)
      .limit(6),
    supabase
      .from('bot_conversations')
      .select('id, phone, full_name, state')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .or(`full_name.ilike.${like},phone.ilike.${like}`)
      .limit(6),
  ]);

  const results: Result[] = [];

  (leadsRes.data ?? []).forEach((l) => {
    results.push({
      kind: 'lead',
      id: l.id,
      title: l.full_name,
      subtitle: [l.company_name, l.phone, l.email].filter(Boolean).join(' · ') || l.status,
      to: `/leads/${l.id}`,
    });
  });
  (customersRes.data ?? []).forEach((c) => {
    results.push({
      kind: 'customer',
      id: c.id,
      title: c.name,
      subtitle: c.industry ?? 'לקוח',
      to: `/customers/${c.id}`,
    });
  });
  (projectsRes.data ?? []).forEach((p) => {
    results.push({
      kind: 'project',
      id: p.id,
      title: p.name,
      subtitle: projectStatusLabel(p.status),
      to: `/projects/${p.id}`,
    });
  });
  (quotesRes.data ?? []).forEach((qr) => {
    results.push({
      kind: 'quote',
      id: qr.id,
      title: qr.quote_number,
      subtitle: `${qr.status} · ₪${String(qr.value_amount)}`,
      to: `/quotes?id=${qr.id}`,
    });
  });
  (convsRes.data ?? []).forEach((c) => {
    results.push({
      kind: 'conversation',
      id: c.id,
      title: c.full_name ?? c.phone,
      subtitle: `${c.phone} · ${c.state}`,
      to: `/bot/conversations/${c.id}`,
    });
  });

  return results;
}

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setActiveIdx(0);
      return;
    }
    inputRef.current?.focus();
  }, [open]);

  // Debounced fetch on query change
  useEffect(() => {
    if (!open || !orgId) return;
    const term = sanitize(query);
    if (term.length < 2) {
      setResults([]);
      setActiveIdx(0);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      void fetchResults(orgId, term).then((rows) => {
        setResults(rows);
        setActiveIdx(0);
        setLoading(false);
      }).catch(() => {
        setResults([]);
        setLoading(false);
      });
    }, 180);
    return () => { clearTimeout(t); };
  }, [open, orgId, query]);

  // Group results by kind, preserve order. Pages and actions are local, so
  // they show from the first keystroke; records still need 2 characters.
  const grouped = useMemo(() => {
    const map = new Map<ResultKind, Result[]>();
    for (const r of [...matchStatic(query), ...results]) {
      const list = map.get(r.kind) ?? [];
      list.push(r);
      map.set(r.kind, list);
    }
    const ordered: Result[] = [];
    for (const k of KIND_ORDER) {
      const list = map.get(k);
      if (list) ordered.push(...list);
    }
    return ordered;
  }, [query, results]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(grouped.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const active = grouped[activeIdx];
        if (active) {
          navigate(active.to);
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [open, onClose, navigate, grouped, activeIdx]);

  if (!open) return null;

  // Build sectioned render
  const sections: { kind: ResultKind; items: Result[] }[] = [];
  for (const k of KIND_ORDER) {
    const items = grouped.filter((r) => r.kind === k);
    if (items.length > 0) sections.push({ kind: k, items });
  }

  // Track global index for active highlight
  let globalIdx = -1;

  return (
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={styles.card}
        onClick={(e) => { e.stopPropagation(); }}
        role="dialog"
        aria-modal="true"
        aria-label="חיפוש מהיר"
      >
        <div className={styles.searchRow}>
          <Search size={18} className={styles.searchIcon} aria-hidden="true" />
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            placeholder="חפש דף, לקוח, פרויקט, ליד, הצעת מחיר…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); }}
          />
          <span className={styles.kbd}>Esc</span>
        </div>

        <div className={styles.results}>
          {grouped.length === 0 && query.length < 2 ? (
            <div className={styles.empty}>
              <span className={styles.emptyTitle}>הקלד 2 תווים לפחות</span>
              <span className={styles.emptySub}>חיפוש לידים, לקוחות, פרויקטים, הצעות מחיר ושיחות בוט.</span>
            </div>
          ) : loading && grouped.length === 0 ? (
            <div className={styles.empty}><span className={styles.emptySub}>מחפש...</span></div>
          ) : grouped.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyTitle}>לא נמצאו תוצאות</span>
              <span className={styles.emptySub}>נסה חיפוש אחר.</span>
            </div>
          ) : (
            sections.map(({ kind, items }) => {
              const Icon = KIND_ICONS[kind];
              return (
                <div key={kind} className={styles.section}>
                  <div className={styles.sectionHead}>{KIND_LABELS[kind]}</div>
                  {items.map((r) => {
                    globalIdx += 1;
                    const isActive = globalIdx === activeIdx;
                    return (
                      <button
                        key={`${kind}-${r.id}`}
                        type="button"
                        className={cn(styles.resultRow, isActive && styles.resultRowActive)}
                        onClick={() => {
                          navigate(r.to);
                          onClose();
                        }}
                      >
                        <Icon size={14} aria-hidden="true" className={styles.resultIcon} />
                        {/* bdi: titles and subtitles carry phones, emails, quote numbers and ₪ amounts */}
                        <bdi className={styles.resultTitle}>{r.title}</bdi>
                        <bdi className={styles.resultSubtitle}>{r.subtitle}</bdi>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
